import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Preferences, PreferencesUpdate } from '../types';
import { preferencesApi } from '../services/api';

interface PreferencesState {
  preferences: Preferences | null;
  isLoading: boolean;

  fetchPreferences: () => Promise<void>;
  updatePreferences: (data: PreferencesUpdate) => Promise<void>;
  setLocalPreference: <K extends keyof PreferencesUpdate>(key: K, value: PreferencesUpdate[K]) => void;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      preferences: null,
      isLoading: false,

      fetchPreferences: async () => {
        set({ isLoading: true });
        try {
          const preferences = await preferencesApi.get();
          set({ preferences, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          console.error('Failed to fetch preferences:', error);
        }
      },

      updatePreferences: async (data: PreferencesUpdate) => {
        const current = get().preferences;
        if (!current) return;

        // Optimistic update
        set({
          preferences: { ...current, ...data, updated_at: new Date().toISOString() },
        });

        try {
          const updated = await preferencesApi.update(data);
          set({ preferences: updated });
        } catch (error) {
          // Revert on error
          set({ preferences: current });
          console.error('Failed to update preferences:', error);
        }
      },

      setLocalPreference: (key, value) => {
        const current = get().preferences;
        if (!current) return;

        set({
          preferences: {
            ...current,
            [key]: value,
            updated_at: new Date().toISOString(),
          },
        });
      },
    }),
    {
      name: 'preferences-storage',
      partialize: (state) => ({ preferences: state.preferences }),
      merge: (persisted: any, current) => {
        const merged = { ...current, ...persisted };
        // Ensure new preference fields have defaults when loaded from old cache
        if (merged.preferences && !merged.preferences.reporting_style) {
          merged.preferences = { ...merged.preferences, reporting_style: 'narrative', exam_notes: '' };
        }
        return merged as PreferencesState;
      },
    }
  )
);
